import type { IssueData } from "zod";
import { z } from "zod";

import { datadogEnvSchema } from "@src/components/sdl/DatadogEnvConfig/DatadogEnvConfig";
import { isLogCollectorService } from "@src/components/sdl/LogCollectorControl/LogCollectorControl";
import { memoryUnits, storageUnits, validationConfig } from "@src/utils/akash/units";
import { ENDPOINT_NAME_VALIDATION_REGEX } from "@src/utils/deploymentData/v1beta3";
import { kvArrayToObject } from "@src/utils/keyValue/keyValue";
import { roundDecimal } from "@src/utils/mathHelpers";
import { isReservedSdlValue, isSdlReference, isValidSecretName } from "@src/utils/sdl/sdlSecrets";
import { isVmImage } from "@src/utils/sdl/vmImages";
import { bytesToShrink } from "@src/utils/unitUtils";

export const VALID_IMAGE_NAME =
  /^(?:(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])(?:(?:\.(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]))+)?(?::[0-9]+)?\/)?[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?(?:\/[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?)*(?::[a-zA-Z0-9_.-]+)?(?:@[a-zA-Z0-9_.:+-]+)?$/;

/**
 * Returns the indexes of every item whose `selectKey` value is shared by more
 * than one item, including the first occurrence — so each row in a duplicate
 * group can be flagged, not just the later ones.
 */
function findDuplicateIndexes<T>(items: T[], selectKey: (item: T) => string): Set<number> {
  const firstIndexByKey = new Map<string, number>();
  const duplicateIndexes = new Set<number>();
  items.forEach((item, index) => {
    const key = selectKey(item);
    const firstIndex = firstIndexByKey.get(key);
    if (firstIndex === undefined) {
      firstIndexByKey.set(key, index);
    } else {
      duplicateIndexes.add(firstIndex);
      duplicateIndexes.add(index);
    }
  });
  return duplicateIndexes;
}

export const ProfileGpuModelSchema = z.object({
  vendor: z.string().min(1, { message: "Vendor is required." }),
  name: z.string().optional(),
  memory: z.string().optional(),
  interface: z.string().optional()
});

export const ServiceStorageSchema = z.object({
  size: z.number({ invalid_type_error: "Storage is required.", required_error: "Storage is required." }).min(1, { message: "Storage is required." }).default(1),
  unit: z.string().min(1, { message: "Storage unit is required." }).default("Gi"),
  isPersistent: z.boolean().optional().default(false),
  name: z
    .string()
    .regex(/^[a-z0-9-]+$/, { message: "Invalid storage name. It must only be lower case letters, numbers and dashes." })
    .regex(/^[a-z]/, { message: "Invalid starting character. It can only start with a lowercase letter." })
    .regex(/[^-]$/, { message: "Invalid ending character. It can only end with a lowercase letter or number" })
    .optional()
    .or(z.literal("")),
  type: z.string().optional(),
  mount: z
    .string()
    .regex(/^\/.*$/, { message: "Mount must be an absolute path." })
    .optional()
    .or(z.literal("")),
  isReadOnly: z.boolean().optional()
});

export const CommandSchema = z.object({
  command: z.string().optional(),
  arg: z.string().optional(),
  importedCommand: z.array(z.string()).optional(),
  importedArg: z.array(z.string()).optional()
});

/** Env key under which the managed SSH public key is mirrored onto every service. */
export const SSH_PUBKEY_ENV_KEY = "SSH_PUBKEY";

/**
 * Env var keys the user may not set themselves. The matching values are managed
 * elsewhere (e.g. `SSH_PUBKEY` is mirrored from the SSH key field) and the managed
 * entry is exempt from the check via the `id === key` sentinel it carries.
 */
export const RESERVED_ENV_KEYS = [SSH_PUBKEY_ENV_KEY] as const;

export const SSH_KEY_REQUIRED_MESSAGE = "SSH Public key is required.";

export const NO_PUBLIC_PORT_MESSAGE = "Set at least one port to Public. Providers refuse a deployment whose ports are all internal.";

export const RESERVED_ENV_VALUE_MESSAGE = 'Plain values may not start with "ac-", which the console reserves for secret references.';

export const SECRET_NAME_MESSAGE = "A secret name may only contain letters, digits and underscores, cannot start with a digit and is at most 64 characters.";

/** Kubernetes' own env var name grammar, which the api's patch route holds a quoting-window edit to. */
const ENV_KEY_PATTERN = /^[-._a-zA-Z][-._a-zA-Z0-9]*$/;

export const ENV_KEY_MESSAGE = "A variable name may only contain letters, digits, dots, dashes and underscores, and cannot start with a digit.";

export const EnvironmentVariableSchema = z
  .object({
    id: z.string().optional(),
    key: z.string().min(1, { message: "Key is required." }),
    value: z.string().optional(),
    isSecret: z.boolean().optional()
  })
  .superRefine((env, ctx) => {
    const isReserved = (RESERVED_ENV_KEYS as readonly string[]).includes(env.key);
    const isManagedEntry = env.id === env.key;
    if (isReserved && !isManagedEntry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["key"],
        message: `"${env.key}" is a reserved variable name`
      });
    }
    if (!env.isSecret && env.value !== undefined && isReservedSdlValue(env.value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["value"], message: RESERVED_ENV_VALUE_MESSAGE });
    }
    const isCheckedAsSecretName = env.isSecret && !isSdlReference(env.value ?? "");
    if (isCheckedAsSecretName && !isValidSecretName(env.key.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["key"], message: SECRET_NAME_MESSAGE });
    }
    if (!isCheckedAsSecretName && !ENV_KEY_PATTERN.test(env.key.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["key"], message: ENV_KEY_MESSAGE });
    }
  });

export const ToSchema = z.object({
  id: z.string().optional(),
  value: z.string().min(1, { message: "Value is required." })
});

export const AcceptSchema = z.object({
  id: z.string().optional(),
  value: z.string().min(1, { message: "Value is required." })
});

export const PROXY_BUFFER_SIZE_MAX = 131072;
export const PROXY_BUFFERS_NUMBER_MAX = 16;
export const PROXY_BUFFERS_SIZE_MAX = 131072;
export const PROXY_BUSY_BUFFERS_SIZE_MAX = 262144;

type ProxyBufferGroup = { bufferSize?: number; buffersNumber?: number; buffersSize?: number; busyBuffersSize?: number };

/** Reports why the provider (nginx_gateway.go proxyBufferLines) would silently drop the proxy_buffer group, or undefined when it is valid or absent. Mirrors the provider: an unset size fills from the other, an unset count defaults to 8, a 0 reads as unset, and busy defaults to the minimum. */
function proxyBufferGroupIssue(proxy: ProxyBufferGroup): { path: string; message: string } | undefined {
  const bufferSize = proxy.bufferSize || undefined;
  const busy = proxy.busyBuffersSize || undefined;
  const effectiveBufferSize = bufferSize ?? proxy.buffersSize;
  const effectiveBuffersSize = proxy.buffersSize ?? bufferSize;
  const effectiveBuffersNumber = proxy.buffersNumber ?? 8;

  if (busy !== undefined && effectiveBufferSize === undefined) {
    return { path: "busyBuffersSize", message: "Set a buffer size or buffers size before busy buffers size." };
  }
  if (effectiveBufferSize === undefined || effectiveBuffersSize === undefined) return undefined;

  const minBusy = Math.max(effectiveBufferSize, effectiveBuffersSize);
  const maxBusy = (effectiveBuffersNumber - 1) * effectiveBuffersSize;
  if (minBusy > maxBusy) {
    return { path: "busyBuffersSize", message: "These buffer settings leave no room for busy buffers; increase buffers number or buffers size." };
  }
  if (busy !== undefined && (busy < minBusy || busy > maxBusy)) {
    return { path: "busyBuffersSize", message: `Busy buffers size must be between ${minBusy} and ${maxBusy} bytes for these buffer settings.` };
  }

  return undefined;
}

export const ServiceExposeHTTPProxySchema = z
  .object({
    bufferingDisable: z.boolean().optional(),
    bufferSize: z
      .number()
      .int()
      .min(0)
      .max(PROXY_BUFFER_SIZE_MAX, { message: `Buffer size must be at most ${PROXY_BUFFER_SIZE_MAX} bytes.` })
      .optional(),
    buffersNumber: z
      .number()
      .int()
      .min(2, { message: "Buffers number must be at least 2." })
      .max(PROXY_BUFFERS_NUMBER_MAX, { message: `Buffers number must be at most ${PROXY_BUFFERS_NUMBER_MAX}.` })
      .optional(),
    buffersSize: z
      .number()
      .int()
      .min(1, { message: "Buffers size must be at least 1 byte." })
      .max(PROXY_BUFFERS_SIZE_MAX, { message: `Buffers size must be at most ${PROXY_BUFFERS_SIZE_MAX} bytes.` })
      .optional(),
    busyBuffersSize: z
      .number()
      .int()
      .min(0)
      .max(PROXY_BUSY_BUFFERS_SIZE_MAX, { message: `Busy buffers size must be at most ${PROXY_BUSY_BUFFERS_SIZE_MAX} bytes.` })
      .optional(),
    connectTimeout: z.number().int().min(0).optional()
  })
  .superRefine((proxy, ctx) => {
    const hasNumber = proxy.buffersNumber !== undefined;
    const hasSize = proxy.buffersSize !== undefined;
    if (hasNumber !== hasSize) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Buffers number and buffers size must be set together.",
        path: [hasNumber ? "buffersSize" : "buffersNumber"]
      });
      return;
    }

    const issue = proxyBufferGroupIssue(proxy);
    if (issue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: [issue.path] });
    }
  });

/** Whether a port carries any explicitly set proxy value, so a hidden `proxy` block is never left validating with no field to show its error. */
export function hasProxyValues(proxy: ServiceExposeHTTPProxyType | undefined): boolean {
  return !!proxy && Object.values(proxy).some(value => value !== undefined);
}

export const ServiceExposeHTTPOptionsSchema = z.object({
  maxBodySize: z.number().min(1, { message: "Max body size is required." }),
  readTimeout: z.number().min(1, { message: "Read timeout is required." }),
  sendTimeout: z.number().min(1, { message: "Send timeout is required." }),
  nextTries: z.number().min(1, { message: "Next tries is required." }),
  nextTimeout: z.number().min(0, { message: "Next timeout can't be negative." }),
  nextCases: z.array(z.string()).min(1, { message: "Next cases is required." }),
  proxy: ServiceExposeHTTPProxySchema.optional()
});

export const PlacementAttributeSchema = z.object({
  id: z.string().optional(),
  key: z.string().min(1, { message: "Key is required." }),
  value: z.string().min(1, { message: "Value is required." })
});

export const SignedBySchema = z.object({
  id: z.string().optional(),
  value: z.string().min(1, { message: "Value is required." })
});

export const CUSTOM_HOST_ID = "__CUSTOM__";
export const CredentialsSchema = z
  .object({
    host: z
      .string()
      .min(1, { message: "Host is required." })
      .refine(
        value => {
          if (value === CUSTOM_HOST_ID) return true;
          try {
            new URL(value.startsWith("http://") || value.startsWith("https://") ? value : `https://${value}`);
            return true;
          } catch {
            return false;
          }
        },
        { message: "Host is not a valid registry URL" }
      )
      .default("docker.io"),
    username: z.string().min(1, { message: "Registry username is required." }),
    password: z.string().min(6, { message: "Registry password must be at least 6 characters." })
  })
  .optional();

/** The architectures an SDL may ask for. Omitting the attribute is a third state, and the only one that writes nothing. */
export const CPU_ARCHITECTURES = ["amd64", "arm64"] as const;

export const ProfileSchema = z
  .object({
    cpu: z.number({ invalid_type_error: "CPU count is required." }).min(0.1, { message: "Minimum amount of CPU for a single service instance is 0.1." }),
    /** Requested CPU architecture. Undefined writes no `arch` attribute at all, which is what every pre-arm64 deployment has. */
    arch: z.enum(CPU_ARCHITECTURES).optional(),
    hasGpu: z.boolean().optional(),
    gpu: z.number({ invalid_type_error: "GPU amount is required." }).optional(),
    gpuModels: z.array(ProfileGpuModelSchema).optional(),
    /** GPU interconnect opt-in. Presence = enabled; `group` set = explicit named group, absent = implicit "auto" group. */
    interconnect: z.object({ group: z.string().optional() }).optional(),
    ram: z.number({ invalid_type_error: "RAM is required." }),
    ramUnit: z.string().min(1, { message: "RAM unit is required." }),
    storage: z.array(ServiceStorageSchema).min(1, { message: "Storage is required." })
  })
  .superRefine((data, ctx) => {
    const customIssues: IssueData[] = [];

    if (data.hasGpu && !data.gpu) {
      customIssues.push({ code: z.ZodIssueCode.custom, message: "Gpu amount is required.", path: ["gpu"], fatal: true });
    }

    if (data.storage.length > 1) {
      data.storage.forEach((storage, index) => {
        if (index === 0) {
          return;
        }

        if (!storage.size || storage.size < 1) {
          customIssues.push({ code: z.ZodIssueCode.custom, message: "Storage amount is required", path: ["storage", index, "size"], fatal: true });
        }

        if (!storage.unit) {
          customIssues.push({ code: z.ZodIssueCode.custom, message: "Storage unit is required", path: ["storage", index, "unit"], fatal: true });
        }

        if (!storage.name) {
          customIssues.push({ code: z.ZodIssueCode.custom, message: "Storage name is required", path: ["storage", index, "name"], fatal: true });
        }

        if (!storage.type) {
          customIssues.push({ code: z.ZodIssueCode.custom, message: "Storage type is required", path: ["storage", index, "type"], fatal: true });
        }

        if (!storage.mount) {
          customIssues.push({ code: z.ZodIssueCode.custom, message: "Storage mount is required", path: ["storage", index, "mount"], fatal: true });
        }
      });

      const namedStorages = data.storage.map((storage, index) => ({ storage, index })).filter(({ storage, index }) => index > 0 && !!storage.name);

      for (const position of findDuplicateIndexes(namedStorages, ({ storage }) => storage.name ?? "")) {
        customIssues.push({
          code: z.ZodIssueCode.custom,
          message: "Storage name must be unique",
          path: ["storage", namedStorages[position].index, "name"],
          fatal: true
        });
      }

      const mountedStorages = data.storage.map((storage, index) => ({ storage, index })).filter(({ storage, index }) => index > 0 && !!storage.mount);

      for (const position of findDuplicateIndexes(mountedStorages, ({ storage }) => storage.mount ?? "")) {
        customIssues.push({
          code: z.ZodIssueCode.custom,
          message: "Storage mount must be unique",
          path: ["storage", mountedStorages[position].index, "mount"],
          fatal: true
        });
      }
    }

    if (customIssues.length) {
      customIssues.forEach(issue => {
        ctx.addIssue(issue);
      });

      return z.NEVER;
    }
  });

const Port = z
  .number()
  .multipleOf(1, { message: "Port numbers don't allow decimals." })
  .min(1, { message: "Port number must be at least 1." })
  .max(65535, { message: "Port number must be at most 65535." });

export const ExposeSchema = z.object({
  id: z.string().optional(),
  port: Port,
  as: Port,
  to: z.array(ToSchema).optional(),
  proto: z.enum(["http", "tcp", "udp"]).optional(),
  global: z.boolean().optional(),
  accept: z.array(AcceptSchema).optional(),
  hasCustomHttpOptions: z.boolean().optional(),
  httpOptions: ServiceExposeHTTPOptionsSchema.optional(),
  ipName: z
    .string()
    .regex(ENDPOINT_NAME_VALIDATION_REGEX, {
      message: "Invalid ip name. It must only be lower case letters, numbers and dashes."
    })
    .regex(/^[a-z]/, { message: "Invalid starting character. It can only start with a lowercase letter." })
    .regex(/[^-]$/, { message: "Invalid ending character. It can only end with a lowercase letter or number" })
    .optional()
    .or(z.literal(""))
});

export const PlacementSchema = z.object({
  id: z.string().min(1, { message: "Placement id is required." }),
  name: z
    .string()
    .min(1, { message: "Placement name is required." })
    .regex(/^[a-z0-9-]+$/, { message: "Invalid placement name. It must only be lower case letters, numbers and dashes." })
    .regex(/^[a-z]/, { message: "Invalid starting character. It can only start with a lowercase letter." })
    .regex(/[^-]$/, { message: "Invalid ending character. It can only end with a lowercase letter or number" }),
  regions: z.array(z.string()).optional(),
  attributes: z.array(PlacementAttributeSchema).optional(),
  signedBy: z
    .object({
      allOf: z.array(SignedBySchema),
      anyOf: z.array(SignedBySchema)
    })
    .optional()
});

export const EndpointSchema = z.object({
  id: z.string().min(1, { message: "Endpoint id is required." }),
  name: z
    .string()
    .min(1, { message: "Endpoint name is required." })
    .regex(ENDPOINT_NAME_VALIDATION_REGEX, { message: "Invalid endpoint name. It must only be lower case letters, numbers and dashes." })
    .regex(/^[a-z]/, { message: "Invalid starting character. It can only start with a lowercase letter." })
    .regex(/[^-]$/, { message: "Invalid ending character. It can only end with a lowercase letter or number" })
});

const validateCpuAmount = (value: number, serviceCount: number, context: z.RefinementCtx) => {
  if (serviceCount === 1 && value < 0.1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Minimum amount of CPU for a single service instance is 0.1.",
      path: ["profile", "cpu"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount === 1 && value > validationConfig.maxCpuAmount) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum amount of CPU for a single service instance is ${validationConfig.maxCpuAmount}.`,
      path: ["profile", "cpu"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount > 1 && serviceCount * value > validationConfig.maxGroupCpuCount) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum total amount of CPU for a single service instance group is ${validationConfig.maxGroupCpuCount}.`,
      path: ["profile", "cpu"],
      fatal: true
    });
    return z.NEVER;
  }

  return true;
};

const validateGpuAmount = (value: number, serviceCount: number, context: z.RefinementCtx) => {
  if (value < 1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "GPU amount must be greater than 0.",
      path: ["profile", "gpu"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount === 1 && value > validationConfig.maxGpuAmount) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum amount of GPU for a single service instance is ${validationConfig.maxGpuAmount}.`,
      path: ["profile", "gpu"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount > 1 && serviceCount * value > validationConfig.maxGroupGpuCount) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum total amount of GPU for a single service instance group is ${validationConfig.maxGroupGpuCount}.`,
      path: ["profile", "gpu"],
      fatal: true
    });
    return z.NEVER;
  }
};

const validateMemoryAmount = (value: number, ramUnit: string, serviceCount: number, context: z.RefinementCtx) => {
  const currentUnit = memoryUnits.find(u => ramUnit.toLowerCase() === u.suffix.toLowerCase());
  const _value = (value || 0) * (currentUnit?.value || 0);
  const maxValue = bytesToShrink(validationConfig.maxMemory);
  const maxGroupValue = bytesToShrink(validationConfig.maxGroupMemory);

  if (serviceCount === 1 && _value < validationConfig.minMemory) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Minimum amount of memory for a single service instance is 1 Mi.",
      path: ["profile", "ram"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount === 1 && _value > validationConfig.maxMemory) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum amount of memory for a single service instance is ${roundDecimal(maxValue.value, 2)} ${maxValue.unit}.`,
      path: ["profile", "ram"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount > 1 && serviceCount * _value > validationConfig.maxGroupMemory) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum total amount of memory for a single service instance group is ${roundDecimal(maxGroupValue.value, 2)} ${maxGroupValue.unit}.`,
      path: ["profile", "ram"],
      fatal: true
    });
    return z.NEVER;
  }
};

const validateStorageAmount = (value: number, storageUnit: string, serviceCount: number, context: z.RefinementCtx) => {
  const currentUnit = storageUnits.find(u => storageUnit.toLowerCase() === u.suffix.toLowerCase());
  const _value = (value || 0) * (currentUnit?.value || 0);
  const maxValue = bytesToShrink(validationConfig.maxStorage);

  if (serviceCount === 1 && _value < validationConfig.minStorage) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Minimum amount of storage for a single service instance is 5 Mi.",
      path: ["profile", "storage", 0, "size"],
      fatal: true
    });
    return z.NEVER;
  } else if (serviceCount === 1 && _value > validationConfig.maxStorage) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Maximum amount of storage for a single service instance is ${roundDecimal(maxValue.value, 2)} ${maxValue.unit}.`,
      path: ["profile", "storage", 0, "size"],
      fatal: true
    });
    return z.NEVER;
  }
};

export const ServiceSchema = z
  .object({
    id: z.string().min(1, { message: "Service id is required." }),
    title: z
      .string()
      .min(1, { message: "Service name is required." })
      .regex(/^[a-z0-9-]+$/, { message: "Invalid service name. It must only be lower case letters, numbers and dashes." })
      .regex(/^[a-z]/, { message: "Invalid starting character. It can only start with a lowercase letter." })
      .regex(/[^-]$/, { message: "Invalid ending character. It can only end with a lowercase letter or number" }),
    image: z.string().min(1, { message: "Docker image name is required." }),
    hasCredentials: z.boolean().optional(),
    credentials: CredentialsSchema,
    profile: ProfileSchema,
    expose: z.array(ExposeSchema),
    command: CommandSchema.optional(),
    env: z.array(EnvironmentVariableSchema).optional(),
    placementId: z.string().min(1, { message: "Placement reference is required." }),
    pricing: z.object({
      amount: z.number().min(1, { message: "Pricing amount is required." }),
      denom: z.string().min(1, { message: "Pricing denom is required." })
    }),
    count: z.number().min(1, { message: "Service count is required." }),
    sshPubKey: z.string().optional(),
    params: z
      .object({
        permissions: z
          .object({
            read: z.array(z.enum(["deployment", "logs", "events"]))
          })
          .optional(),
        // Preserved verbatim through the builder round-trip; not yet editable in the form UI.
        tee: z.enum(["cpu", "cpu-gpu"]).optional()
      })
      .optional()
  })
  .superRefine((data, ctx) => {
    validateCpuAmount(data.profile.cpu, data.count, ctx);
    validateMemoryAmount(data.profile.ram, data.profile.ramUnit, data.count, ctx);
    validateStorageAmount(data.profile.storage[0].size, data.profile.storage[0].unit, data.count, ctx);
    if (data.profile.hasGpu) {
      validateGpuAmount(data.profile.gpu as number, data.count, ctx);
    }
  });

const ImageList = z.object({
  imageList: z.array(z.string()).optional()
});

const SSHKey = z.object({
  hasSSHKey: z.boolean().optional()
});

const logProviderVars = z.discriminatedUnion("PROVIDER", [
  datadogEnvSchema.extend({
    PROVIDER: z.literal("DATADOG")
  })
]);

/** The reclamation windows the dropdown offers; an imported SDL may carry any other duration the SDL schema accepts. */
export const RECLAMATION_MIN_WINDOW_VALUES = ["1h", "4h", "24h", "72h"] as const;
export const ReclamationMinWindowSchema = z.string().regex(/^[1-9][0-9]*(s|m|h)$/);
export type ReclamationMinWindow = z.infer<typeof ReclamationMinWindowSchema>;

export const SdlBuilderFormValuesSchema = z
  .object({
    placements: z.array(PlacementSchema).min(1, { message: "At least one placement is required." }),
    services: z.array(ServiceSchema).min(1, { message: "At least one service is required." }),
    endpoints: z.array(EndpointSchema).optional().default([]),
    // Optional deployment-level reclamation requirement; maps to `reclamation.min_window` in the SDL.
    reclamationMinWindow: ReclamationMinWindowSchema.optional()
  })
  .merge(ImageList)
  .merge(SSHKey)
  .superRefine((data, ctx) => {
    const placementIds = new Set<string>();
    for (let i = 0; i < data.placements.length; i++) {
      const placementId = data.placements[i].id;
      if (placementIds.has(placementId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Placement id must be unique.",
          path: ["placements", i, "id"],
          fatal: true
        });
        continue;
      }
      placementIds.add(placementId);
    }

    for (const index of findDuplicateIndexes(data.placements, placement => placement.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Placement name must be unique.",
        path: ["placements", index, "name"],
        fatal: true
      });
    }

    for (let i = 0; i < data.services.length; i++) {
      if (!placementIds.has(data.services[i].placementId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Service references a placement that does not exist.",
          path: ["services", i, "placementId"],
          fatal: true
        });
      }
    }

    for (const index of findDuplicateIndexes(data.services, service => service.title)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Service name must be unique.",
        path: ["services", index, "title"],
        fatal: true
      });
    }

    for (const index of findDuplicateIndexes(data.endpoints ?? [], endpoint => endpoint.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Endpoint name must be unique.",
        path: ["endpoints", index, "name"]
      });
    }

    if (data.services.length > 0 && !data.services.some(service => service.expose.some(expose => expose.global))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: NO_PUBLIC_PORT_MESSAGE,
        path: ["services", 0, "expose"]
      });
    }

    if (data.imageList && data.imageList.length > 0) {
      for (let i = 0; i < data.services.length; i++) {
        if (!data.imageList.includes(data.services[i].image)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Docker image is not in the valid image list.",
            path: ["services", i, "image"],
            fatal: true
          });
          return z.NEVER;
        }
      }
    } else {
      for (let i = 0; i < data.services.length; i++) {
        if (!VALID_IMAGE_NAME.test(data.services[i].image)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid docker image name.",
            path: ["services", i, "image"],
            fatal: true
          });
          return z.NEVER;
        }
      }
    }

    for (let i = 0; i < data.services.length; i++) {
      if (!isVmImage(data.services[i].image)) {
        continue;
      }
      const expose = data.services[i].expose;
      const managedSshIndex = expose.findIndex(entry => entry.port === 22 && entry.as === 22);
      for (let j = 0; j < expose.length; j++) {
        if (j === managedSshIndex) {
          continue;
        }
        if (expose[j].port === 22 || expose[j].as === 22) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Port 22 is reserved for SSH.",
            path: ["services", i, "expose", j, "port"]
          });
        }
      }
    }

    for (let i = 0; i < data.services.length; i++) {
      if (isVmImage(data.services[i].image) && !data.services[i].sshPubKey?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: SSH_KEY_REQUIRED_MESSAGE,
          path: ["services", i, "sshPubKey"],
          fatal: true
        });
        return z.NEVER;
      }
    }

    for (let i = 0; i < data.services.length; i++) {
      if (isLogCollectorService(data.services[i])) {
        const service = data.services[i];
        const env = kvArrayToObject(service.env || []);
        const result = logProviderVars.safeParse(env);

        if (!result.success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid log provider configuration.",
            path: ["services", i, "env"],
            fatal: true
          });
        }
      }
    }
  });

export type ServiceType = z.infer<typeof ServiceSchema>;
export type SdlBuilderFormValuesType = z.infer<typeof SdlBuilderFormValuesSchema>;
export type ProfileGpuModelType = z.infer<typeof ProfileGpuModelSchema>;
export type CpuArchType = (typeof CPU_ARCHITECTURES)[number];
export type EnvironmentVariableType = z.infer<typeof EnvironmentVariableSchema>;
export type AcceptType = z.infer<typeof AcceptSchema>;
export type PlacementAttributeType = z.infer<typeof PlacementAttributeSchema>;
export type SignedByType = z.infer<typeof SignedBySchema>;
export type ExposeType = z.infer<typeof ExposeSchema>;
export type ServiceExposeHTTPProxyType = z.infer<typeof ServiceExposeHTTPProxySchema>;
export type PlacementType = z.infer<typeof PlacementSchema>;
export type EndpointType = z.infer<typeof EndpointSchema>;
